import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  ATTRIBUTION_SOURCE_LOGOS,
  ATTRIBUTION_SOURCE_OPTIONS,
  AttributionLinkSource,
  type HardcodedLinkSourceValue,
  isAttributionLinkSource,
} from 'common/constants/attribution-link-source';
import { utilCopyToClipboard } from 'common/utils';
import { format } from 'date-fns';
import { useAppSelector } from 'hooks';
import { FC, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { generateTemporaryLinksSlice } from 'store/reducers/temporary-links/generate-temporary-links-slice';

import type { IPageFormV2 } from '../../../../page-builder-v2.types';

interface Props {
  asin: string;
  initialSource?: string | null;
  productIndex: number;
  onClose: () => void;
}

const resolveInitialSource = (initialSource?: string | null): AttributionLinkSource =>
  isAttributionLinkSource(initialSource)
    ? initialSource
    : AttributionLinkSource.PartnerBoost;

export const GenerateLinkModalV2: FC<Props> = ({
  asin,
  initialSource,
  onClose,
  productIndex,
}) => {
  const { control, setValue } = useFormContext<IPageFormV2>();
  const publisher = useWatch({ control, name: 'publisher' });

  const { error, isLoading, links } = useAppSelector(
    ({ generateTemporaryLinks }) => generateTemporaryLinks,
  );
  const dispatch = useDispatch();

  const [expiredAt, setExpiredAt] = useState<string>('');
  const [source, setSource] = useState<AttributionLinkSource>(resolveInitialSource(initialSource));

  const [generatedLink] = [...(links?.created || []), ...(links?.updated || [])];

  const handleGenerate = () => {
    const region = publisher?.country || 'us';

    dispatch(
      generateTemporaryLinksSlice.actions.generate({
        meta: {
          onSuccess: (_id, data) => {
            const d = data as unknown as {
              hardcoded_link_source?: HardcodedLinkSourceValue | null;
              is_hardcoded_link: number;
              page_url: string;
            };
            setValue(`products.${productIndex}.isHardcodedLink`, d.is_hardcoded_link as 0 | 1);
            setValue(`products.${productIndex}.pageUrl`, d.page_url);
            setValue(`products.${productIndex}.hardcodedLinkSource`, d.hardcoded_link_source ?? null);
          },
        },
        payload: {
          asins: [asin],
          ...(expiredAt ? { expired_at: format(new Date(expiredAt), 'yyyy-MM-dd') } : {}),
          publisher_id: publisher?.id || 0,
          region,
          source,
        },
      }),
    );
  };

  useEffect(
    () => () => {
      dispatch(generateTemporaryLinksSlice.actions.resetState());
    },
    [],
  );

  return (
    <Dialog fullWidth maxWidth="sm" onClose={onClose} open>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Generate link
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          minWidth: 0,
          overflowX: 'hidden',
          pt: 2,
        }}
      >
        <Box>
          <Typography color="text.secondary" mb={0.75} variant="caption">ASIN</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField disabled fullWidth size="small" value={asin} />
            <IconButton
              onClick={() => utilCopyToClipboard(asin)}
              size="small"
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box>
          <Typography color="text.secondary" mb={0.75} variant="caption">Provider</Typography>
          <TextField
            disabled={isLoading}
            fullWidth
            onChange={(e) => setSource(e.target.value as AttributionLinkSource)}
            select
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: { maxHeight: 300 },
                },
              },
            }}
            size="small"
            value={source}
          >
            {ATTRIBUTION_SOURCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
                  <Box
                    alt={option.label}
                    component="img"
                    src={ATTRIBUTION_SOURCE_LOGOS[option.value]}
                    sx={{ height: 'auto', width: 16 }}
                  />
                  <span>{option.label}</span>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography color="text.secondary" mb={0.75} variant="caption">Expiration date (optional)</Typography>
          <TextField
            disabled={isLoading}
            fullWidth
            inputProps={{ min: format(new Date(), 'yyyy-MM-dd') }}
            onChange={(e) => setExpiredAt(e.target.value)}
            size="small"
            type="date"
            value={expiredAt}
          />
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && generatedLink?.link && (
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography color="text.secondary" mb={0.75} variant="caption">Generated link</Typography>
            <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, minWidth: 0 }}>
              <Typography
                noWrap
                onClick={() => window.open(generatedLink.link, '_blank')}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  color: 'primary.main',
                  cursor: 'pointer',
                  flex: 1,
                  fontSize: '0.8rem',
                  minWidth: 0,
                  overflow: 'hidden',
                  p: 1,
                  textDecoration: 'underline',
                  textOverflow: 'ellipsis',
                  '&:hover': { opacity: 0.8 },
                }}
                title={generatedLink.link}
              >
                {generatedLink.link}
              </Typography>
              <IconButton
                onClick={() => utilCopyToClipboard(generatedLink.link)}
                size="small"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, flexShrink: 0 }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        {!isLoading && error && (
          <Alert severity="error">{error.message}</Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={isLoading} onClick={onClose} variant="outlined">
          Cancel
        </Button>
        {!generatedLink?.link && (
          <Button disabled={isLoading} onClick={handleGenerate} variant="contained">
            Generate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
